import { type Ref, computed, type ComputedRef, watch } from 'vue';
import type { Item, FilterOption } from '../types/main';
import type { ClientSortOptions, EmitsEventName } from '../types/internal';
import { getItemValue } from '../utils';

export default function useTotalItems(
  clientSortOptions: Ref<ClientSortOptions | null>,
  filterOptions: Ref<FilterOption[] | null>,
  isServerSideMode: ComputedRef<boolean>,
  items: Ref<Item[]>,
  itemsSelected: Ref<Item[] | null>,
  searchField: Ref<string | string[]>,
  searchValue: Ref<string>,
  serverItemsLength: Ref<number>,
  multiSort: Ref<boolean>,
  isSingleSelectable: ComputedRef<boolean>,
  emits: (event: EmitsEventName, ...args: unknown[]) => void,
) {
  const generateSearchingTarget = (item: Item): string => {
    if (typeof searchField.value === 'string' && searchField.value !== '')
      return getItemValue(searchField.value, item);
    if (Array.isArray(searchField.value)) {
      let searchString = '';
      searchField.value.forEach((field) => {
        searchString += getItemValue(field, item);
      });
      return searchString;
    }
    return Object.values(item).join(' ');
  };

  // items searching
  const itemsSearching = computed((): Item[] => {
    // searching feature is not available in server-side mode
    if (!isServerSideMode.value && searchValue.value !== '') {
      const regex = new RegExp(searchValue.value, 'i');
      return items.value.filter((item) =>
        regex.test(generateSearchingTarget(item)),
      );
    }
    return items.value;
  });
  // items filtering
  const itemsFiltering = computed((): Item[] => {
    let itemsFiltered = [...itemsSearching.value];
    if (filterOptions.value) {
      filterOptions.value.forEach((option: FilterOption) => {
        itemsFiltered = itemsFiltered.filter((item) => {
          const { field, comparison, criteria } = option;
          if (typeof comparison === 'function') {
            return comparison(getItemValue(field, item), criteria as string);
          }
          const itemValue = getItemValue(field as string, item);
          switch (comparison) {
            case '=':
              return itemValue === criteria;
            case '!=':
              return itemValue !== criteria;
            case '>':
              return itemValue > criteria;
            case '<':
              return itemValue < criteria;
            case '<=':
              return itemValue <= criteria;
            case '>=':
              return itemValue >= criteria;
            case 'between':
              return (
                itemValue >= Math.min(...criteria) &&
                itemValue <= Math.max(...criteria)
              );
            case 'in':
              return (criteria as (string | number)[]).includes(itemValue);
            default:
              return itemValue === criteria;
          }
        });
      });
      return itemsFiltered;
    }
    return itemsSearching.value;
  });

  watch(
    itemsFiltering,
    (newVal) => {
      if (filterOptions.value) {
        emits('updateFilter', newVal);
      }
    },
    { immediate: true, deep: true },
  );

  function recursionMuiltSort(
    sortByArr: string[],
    sortDescArr: boolean[],
    itemsToSort: Item[],
    index: number,
  ): Item[] {
    const sortBy = sortByArr[index];
    const sortDesc = sortDescArr[index];
    const sorted = (
      index === 0
        ? itemsToSort
        : recursionMuiltSort(sortByArr, sortDescArr, itemsToSort, index - 1)
    ).sort((a: Item, b: Item) => {
      let isAllSame = true;
      for (let i = 0; i < index; i += 1) {
        if (getItemValue(sortByArr[i], a) !== getItemValue(sortByArr[i], b)) {
          isAllSame = false;
          break;
        }
      }
      if (isAllSame) {
        if (
          getItemValue(sortBy as string, a) < getItemValue(sortBy as string, b)
        )
          return sortDesc ? 1 : -1;
        if (
          getItemValue(sortBy as string, a) > getItemValue(sortBy as string, b)
        )
          return sortDesc ? -1 : 1;
        return 0;
      }
      return 0;
    });
    return sorted;
  }

  // flow: searching => filtering => sorting
  // (last step: sorting)
  const totalItems = computed((): Item[] => {
    if (isServerSideMode.value) return items.value;
    if (clientSortOptions.value === null) return itemsFiltering.value;
    const { sortBy, sortDesc } = clientSortOptions.value;
    const itemsFilteringSorted = [...itemsFiltering.value];
    // multi sort
    if (multiSort && Array.isArray(sortBy) && Array.isArray(sortDesc)) {
      if (sortBy.length === 0) return itemsFilteringSorted;
      return recursionMuiltSort(
        sortBy,
        sortDesc,
        itemsFilteringSorted,
        sortBy.length - 1,
      );
    }
    return itemsFilteringSorted.sort((a, b) => {
      if (getItemValue(sortBy as string, a) < getItemValue(sortBy as string, b))
        return sortDesc ? 1 : -1;
      if (getItemValue(sortBy as string, a) > getItemValue(sortBy as string, b))
        return sortDesc ? -1 : 1;
      return 0;
    });
  });

  const totalItemsLength = computed((): number =>
    isServerSideMode.value ? serverItemsLength.value : totalItems.value.length,
  );

  // multiple selecting
  const selectItemsComputed = computed({
    get: () => itemsSelected.value ?? [],
    set: (value) => {
      emits('update:itemsSelected', value);
    },
  });

  const toggleSelectAll = (isChecked: boolean): void => {
    selectItemsComputed.value = isChecked ? totalItems.value : [];
    if (isChecked) emits('selectAll');
  };

  const toggleSelectItem = (item: Item): void => {
    const isAlreadyChecked = item.checkbox;
    delete item.checkbox;
    delete item.index;
    if (!isAlreadyChecked) {
      if (isSingleSelectable.value) {
        selectItemsComputed.value = [item];
        emits('selectRow', item);
        return;
      }
      const selectItemsArr: Item[] = selectItemsComputed.value;
      selectItemsArr.unshift(item);
      selectItemsComputed.value = selectItemsArr;
      emits('selectRow', item);
    } else {
      selectItemsComputed.value = selectItemsComputed.value.filter(
        (selectedItem) => JSON.stringify(selectedItem) !== JSON.stringify(item),
      );
      emits('deselectRow', item);
    }
  };

  return {
    totalItems,
    selectItemsComputed,
    totalItemsLength,
    toggleSelectAll,
    toggleSelectItem,
  };
}
